import { useOfficeProfile } from '~/domain/LocationSettings';
import { useUserSelector } from '~/hooks/useUserSelector';
import type { IUser } from '~/models';

interface IBillingOffice {
  /** The office's full record; `undefined` until it has loaded. */
  office: IUser | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * The office a payment method belongs to, as a full record: its contact and address
 * go on a new card or bank account, and `is_pre_approved` lifts the payment rules.
 *
 * The office selected in the header is already in redux. Any other office (a location
 * Billing Details was opened for, GOTU-19472, or a job's owner) is read with its own
 * `x-office`: its `childOffices` row is slim — no email, phone or `is_pre_approved` —
 * and an id missing from that list would silently fall back to the header office.
 *
 * @param officeId - the office asked about; the one selected in the header when omitted
 */
export const useBillingOffice = (officeId?: string): IBillingOffice => {
  const headerOffice = useUserSelector() as IUser | undefined;
  const otherOfficeId = officeId && officeId !== headerOffice?.id ? officeId : '';
  const { data, isError, isPlaceholderData } = useOfficeProfile(otherOfficeId);

  if (!otherOfficeId) {
    return { office: headerOffice, isLoading: false, isError: false };
  }

  const office = isPlaceholderData ? undefined : data;

  return { office, isLoading: !office && !isError, isError };
};
